/* eslint-disable import/prefer-default-export */
import { getTaskStatusDisplay, getTaskTypeDisplay } from "configs/tasks";
import { NEXT_TICK } from "constants/schedule-monitor.constants";
import { ITask, ITaskRowViewModel } from "types/task";

/**
 * Parses the wire's `Date | string | undefined` into a real `Date`, or `null` when
 * absent/unparseable. Never returns an Invalid Date - formatting one throws a RangeError.
 */
const toValidDate = (value: Date | string | undefined): Date | null => {
    if (!value) {
        return null;
    }
    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
};

export const toTaskRowViewModel = (task: ITask): ITaskRowViewModel => {
    const createdAt = toValidDate(task.createdAt);
    // `scheduledDateTime` stays null for NEXT_TICK - it feeds failed/stalled derivation, which
    // already treats a null scheduledDateTime as "due now" and must not be told otherwise here.
    // `displayScheduledDateTime` is purely for the table: fall back to `created` so the row isn't
    // blank.
    const scheduledDateTime = task.scheduledDateTime === NEXT_TICK ? null : toValidDate(task.scheduledDateTime);
    const displayScheduledDateTime = task.scheduledDateTime === NEXT_TICK ? createdAt : scheduledDateTime;

    return {
        id: task.id,
        scheduleId: task.scheduleId,
        typeCode: task.type,
        typeLabel: getTaskTypeDisplay(task.type).label,
        statusCode: task.taskStatus,
        statusLabel: getTaskStatusDisplay(task.taskStatus).label,
        statusColor: getTaskStatusDisplay(task.taskStatus).color,
        scheduledDateTime,
        displayScheduledDateTime,
        createdAt,
        data: (task.data as unknown as Record<string, unknown>) ?? null,
        retryScheduleId: task.meta?.retryScheduleId ?? null,
    };
};

export default toTaskRowViewModel;
